import { inject, singleton } from 'tsyringe';
import ConfigurationManager from '../../infra/configuration/ConfigurationManager';
import { resolveMethodOverride } from '../../infra/overrides/resolveMethodOverride';
import type { SetServiceDependencies } from '../../infra/startup/types';
import { internalLogger } from '../../interface/v1/clientOsLogger';
import {
  TokenLifetimeRequirementType,
  TokenType
} from '../../services/JWeb/JWebEnums';
import { AccessToken, AuthPluginError } from '../../services/JWeb/types';
import bindAllMethods from '../../utils/bindAllMethods';
import { decodeJWTPayload } from '../../utils/tokenUtils/JWTUtils';
import {
  AuthContextEnum,
  AuthTokenService,
  IAuthTokenService
} from '../authTokenService';
import {
  DevicePluginFacade,
  JWebEnums,
  JWebErrorHandler,
  ValueStoreFacade
} from '../JWeb';
import InitializationTokenCache from '../JWeb/cache/InitializationTokenCache';
import { AuthPluginFacade } from '../JWeb/facades/AuthPluginFacade';
import * as JWebEnumsImport from '../JWeb/JWebEnums';
import {
  TokenProviderOptions,
  ValueStoreGetResult,
  ValueStoreGetSuccessResult
} from '../JWeb/types';
import { getUserInteractionEntryPointBasedOnParams } from '../mfeRouterService/operations/NewLoginPathOperation/utils/getUserInteractionEntryPoint';
import { TenantStrategyEnum } from '../tenantHandler/strategy/strategy';
import BaseSessionService from './BaseSessionService';
import { JWEB_SIGNIN_REQUEST_ID } from './constants';
import ExchangeTokenServiceNative from './exchangeTokenService/ExchangeTokenServiceNative';
import IExchangeTokenService from './exchangeTokenService/IExchangeTokenService';
import ISessionService, {
  ForceLoginResponseType,
  ProviderLookupRequiredResultType,
  RefreshParameterType,
  SignInOptionsType,
  SignInResponseType
} from './ISessionService';
import { GenerateAuthenticationUrlParams } from './loginService';
import IRefreshTokenService from './refreshTokenService/IRefreshTokenService';
import SessionObserver, { SessionEvents } from './SessionObserver';
import type { LoginInputType } from './types';
import {
  listenerAuthChangeJWeb,
  promiseLoginFinished
} from './utils/waitLoginToFinish';

@singleton()
class SessionServiceNative
  extends BaseSessionService
  implements ISessionService
{
  private _refreshTokenServiceNative: IRefreshTokenService;
  private _exchangeTokenServiceNative: IExchangeTokenService;
  private _legacySessionDataValue = {
    isLoggedIn: false,
    idToken: ''
  };
  private _loginProps: LoginInputType;
  private valueStoreadditionalAuthorizationParametersKey =
    'clientOSAdditionalAuthorizationParams';

  private _defaultCountry = 'US';
  constructor(
    @inject(AuthTokenService)
    authTokenService: IAuthTokenService,
    @inject(ExchangeTokenServiceNative)
    exchangeTokenServiceNative: IExchangeTokenService,
    @inject('IRefreshTokenService')
    refreshTokenServiceNative: IRefreshTokenService
  ) {
    super({ authTokenService, isNative: true });
    this._exchangeTokenServiceNative = exchangeTokenServiceNative;
    this._refreshTokenServiceNative = refreshTokenServiceNative;
    this._loginProps = ConfigurationManager.getProps('login');

    this.setRefreshMethods({
      init: this._updateInitialState.bind(this),
      refresh: this._updateInitialState.bind(this)
    });

    bindAllMethods(this);
  }

  private extractUserIdentityFromIdToken(idToken: string | null | undefined): {
    email: string | undefined;
    username: string | undefined;
  } {
    if (!idToken || typeof idToken !== 'string') {
      return { email: undefined, username: undefined };
    }

    const userData = decodeJWTPayload(idToken);
    return {
      email: userData?.email,
      username: userData?.preferred_username
    };
  }

  private async _getAdditionalAuthorizationParameters(
    target?: string
  ): Promise<Record<string, string>> {
    let parsedValueStoreadditionalAuthorizationParametersKey: Record<
      string,
      string
    > = {};

    try {
      const valueStoreadditionalAuthorizationParameters =
        (await ValueStoreFacade.get({
          keys: [
            this._loginProps?.overrideClientOSAdditionalAuthorizationParams ||
              this.valueStoreadditionalAuthorizationParametersKey
          ]
        })) as ValueStoreGetResult;

      if (valueStoreadditionalAuthorizationParameters) {
        parsedValueStoreadditionalAuthorizationParametersKey = JSON.parse(
          (
            valueStoreadditionalAuthorizationParameters.result as unknown as ValueStoreGetSuccessResult
          )?.[0].value
        );
      }
    } catch (error) {
      internalLogger.error(
        'Error getting additionalAuthorizationParameters from ValueStore',
        error
      );
    }

    const country =
      (await DevicePluginFacade.getDeviceInfoCached())?.locale?.country ||
      this._defaultCountry;
    const additionalAuthorizationParameters = {
      ...parsedValueStoreadditionalAuthorizationParametersKey,
      country
    };

    if (target) {
      additionalAuthorizationParameters['target'] = target;
    }

    return additionalAuthorizationParameters;
  }

  public async forceLogin(): Promise<ForceLoginResponseType> {
    try {
      const currentTenant = this._tenantHandlerService?.getTenantByLevel();

      // Get current user info before force login
      const currentIdToken = this.getIdToken();
      const { email: previousEmail, username: previousUsername } =
        this.extractUserIdentityFromIdToken(currentIdToken);

      internalLogger.debug?.d4({
        className: 'SessionServiceNative',
        methodName: 'forceLogin',
        message: 'Initiating native force login',
        variables: {
          currentTenantId: currentTenant?.id,
          previousEmail,
          previousUsername
        }
      });

      const additionalAuthorizationParameters =
        await this._getAdditionalAuthorizationParameters();

      const tokenResult = await AuthPluginFacade.getToken({
        tokenProviderOptions: {
          tokenType: TokenType.user,
          skipTokenRefresh: true,
          allowUserInteraction: true,
          tokenLifetimeRequirements: [
            {
              type: TokenLifetimeRequirementType.preferredMaximumSecondsSinceIssued,
              timeInterval: 0
            }
          ],
          persistOrgAwareIfSameAcct: true,
          requestId: JWEB_SIGNIN_REQUEST_ID,
          additionalAuthorizationParameters
        }
      });

      if ((tokenResult as AuthPluginError).error) {
        internalLogger.error?.({
          className: 'SessionServiceNative',
          methodName: 'forceLogin',
          message: 'Force login failed',
          variables: {
            error: (tokenResult as AuthPluginError).error
          }
        });
        return {
          success: false,
          error: {
            message: (tokenResult as AuthPluginError).error.code
          }
        };
      }

      const newToken = tokenResult as AccessToken;

      // Get new user info after force login
      const newIdToken = newToken?.account?.idToken;
      const { email: newEmail, username: newUsername } =
        this.extractUserIdentityFromIdToken(newIdToken);

      // Check if user changed (different email or preferred_username)
      const userChanged =
        (previousEmail && newEmail && previousEmail !== newEmail) ||
        (previousUsername && newUsername && previousUsername !== newUsername);

      internalLogger.debug?.d4({
        className: 'SessionServiceNative',
        methodName: 'forceLogin',
        message: 'Native force login successful',
        variables: {
          tenantId: 'tenantId' in newToken ? newToken.tenantId : undefined,
          userChanged,
          newEmail,
          newUsername
        }
      });

      if (!userChanged) {
        // Cache the new access token we just received
        const newAccessTokenValue = newToken?.tokenValue;
        if (newAccessTokenValue) {
          const authContext = this._authTokenService.getTenantIdFromGivenToken(
            newAccessTokenValue
          )
            ? AuthContextEnum.tenant
            : AuthContextEnum.tenantless;
          this._authTokenService.setToken(newAccessTokenValue, authContext);
        }

        if (
          currentTenant?.id &&
          (!newToken.tenantId || newToken.tenantId !== currentTenant?.id)
        ) {
          await this._tenantHandlerService?.setTenant(
            currentTenant?.id,
            currentTenant?.level ?? 0,
            { reload: false }
          );
        }
      }

      return {
        success: true
      };
    } catch (error) {
      internalLogger.error?.({
        className: 'SessionServiceNative',
        methodName: 'forceLogin',
        message: 'Error during native force login',
        variables: { error }
      });
      throw error;
    }
  }

  async signIn(options: SignInOptionsType): Promise<SignInResponseType> {
    if (this.isLoggedIn()) {
      internalLogger?.debug('User is already logged in.');
      return {
        success: false,
        error: {
          message: 'userAlreadyLoggedIn'
        }
      };
    }

    const { target, authorizationEndpoint, emailHint } = options || {};
    const additionalAuthorizationParameters =
      await this._getAdditionalAuthorizationParameters(target);

    // TODO: we should remove any
    const tokenProviderOptions: TokenProviderOptions | any = {
      tokenType: JWebEnums.TokenType.user,
      allowNetworkAccess: true,
      allowUserInteraction: true,
      requireFreshToken: true,
      showAccountCreationLink: true,
      skipTokenRefresh: false,
      userInteractionEntryPoint: JWebEnums.UserInteractionEntryPoint.signIn,
      requestId: JWEB_SIGNIN_REQUEST_ID,
      additionalAuthorizationParameters,
      emailHint
    };

    if (authorizationEndpoint) {
      tokenProviderOptions.loginBaseUrl = authorizationEndpoint;
    }

    listenerAuthChangeJWeb();

    // 1. We request the new login
    const response = await AuthPluginFacade.getToken({ tokenProviderOptions });

    if ((response as AuthPluginError).error) {
      return {
        success: false,
        error: {
          message: (response as AuthPluginError).error.code
        }
      };
    }
    internalLogger?.debug(
      'JWeb.GetToken was returned, waiting for fake refresh to finish.'
    );
    // 2. The auth.change event will be received, meanwhile, we wait for the login to finish.
    await promiseLoginFinished;

    return {
      success: true
    };
  }

  public setDependencies({ services, ...rest }: SetServiceDependencies): void {
    super.setDependencies({ services, ...rest });

    bindAllMethods(this);
  }

  public async init(): Promise<void> {
    /* istanbul ignore next */
    internalLogger.log('Initializing session service native.');

    await this._updateInitialState();

    this._markAsReady();
    return Promise.resolve();
  }

  public getLoginCooldown(): number {
    return this._criticalScopeHandler.getLoginCooldown();
  }

  /** This should be used to ensure that critical scopes is invalid and the login is not in cooldown. */
  public goForceLogin(waitCallback = () => {}): void {
    // Only if the isCriticalScopesValid is false and the getLoginCooldown is false, we redirect to login.
    if (this._criticalScopeHandler.ensureCriticalScopes(waitCallback)) {
      this._loginService.redirectToLogin();
    }
  }

  public async refresh(
    refreshParameterType?: RefreshParameterType
  ): Promise<void> {
    if (!this.isLoggedIn()) {
      return;
    }

    const override = resolveMethodOverride('session', 'refresh');
    if (override) {
      internalLogger.debug.d4({
        className: 'SessionServiceNative',
        methodName: 'refresh',
        message: 'Overriding refresh method with custom implementation',
        isAsyncMethod: true,
        parameters: { refreshParameterType }
      });

      await override(refreshParameterType);

      // Notify observers that refresh occurred (keeps listeners aligned)
      await SessionObserver.notify(
        SessionEvents.REFRESH_TOKEN,
        this._authTokenService.getToken()?.token
      );
      return;
    }

    // if (this._criticalScopeHandler.getLoginCooldown()) {
    //   /* istanbul ignore next */
    //   internalLogger.debug('Login cooldown is active, skipping refresh token');
    //   return;
    // }
    return this._refreshTokenServiceNative.refresh(refreshParameterType);
  }

  private _getTokenProviderOptions(
    tokenProviderOptions: TokenProviderOptions
  ): TokenProviderOptions {
    const options = { ...tokenProviderOptions };

    if (options.requireFreshToken) {
      options.tokenLifetimeRequirements = [
        {
          type: TokenLifetimeRequirementType.preferredMaximumSecondsSinceIssued,
          timeInterval: 0
        }
      ];
    }

    if (options.requireFreshToken && options.skipTokenRefresh) {
      options.scopesRequested = ['openid'];
    }

    return options;
  }

  private async _exchangeNativeForceLoginToken(): Promise<
    AuthPluginError | AccessToken
  > {
    const exchangeResult = await (
      this._exchangeTokenServiceNative as ExchangeTokenServiceNative
    ).exchangeJWeb(
      null,
      this._getTokenProviderOptions({
        tokenType: TokenType.user,
        allowUserInteraction: true,
        skipTokenRefresh: true,
        requireFreshToken: true
      })
    );

    return exchangeResult;
  }

  public async redirectToIdentityProvider(): Promise<
    AuthPluginError | AccessToken
  > {
    const entryPoint = getUserInteractionEntryPointBasedOnParams() || 'signIn';

    // If the user is not logged in.
    if (!this.isLoggedIn()) {
      const country =
        (await DevicePluginFacade.getDeviceInfoCached())?.locale?.country ||
        this._defaultCountry;

      // ? This is the login of the user in login flow. We dont need to setToken here, because we can get affter of the /loggedin flow.
      const signInResult = await AuthPluginFacade.getToken({
        tokenProviderOptions: this._getTokenProviderOptions({
          userInteractionEntryPoint:
            JWebEnumsImport.UserInteractionEntryPoint[entryPoint],
          tokenType: TokenType.user,
          allowUserInteraction: true,
          additionalAuthorizationParameters: {
            country
          },
          requestId: JWEB_SIGNIN_REQUEST_ID
        })
      });

      return signInResult;
    }
    // So, the user is logged in, we should apply the force Login.
    return this._exchangeNativeForceLoginToken();
  }

  public async exchangeToken(
    tenantId: string,
    authContext: AuthContextEnum,
    tenantStrategy: TenantStrategyEnum
  ): Promise<void> {
    return this._exchangeTokenServiceNative.exchange({
      tenantId,
      authContext,
      tenantStrategy
    });
  }

  public async isProviderLookupRequired(): Promise<ProviderLookupRequiredResultType> {
    const result = await AuthPluginFacade.isIdentityProviderLookupEnabled();
    return { value: result.value };
  }

  public async clearSession(): Promise<void> {
    this._legacySessionDataValue.isLoggedIn = false;
    this._legacySessionDataValue.idToken = '';
    await AuthPluginFacade.logout().then((v) => JWebErrorHandler(v));
  }

  public async logout(): Promise<void> {
    await this.clearSession();
  }

  public async generateAuthenticationUrl(
    options: GenerateAuthenticationUrlParams
  ): Promise<string> {
    return this._loginService.generateAuthenticationUrl(options);
  }

  public isLoggedIn(): boolean {
    const override = resolveMethodOverride('session', 'isLoggedIn');
    if (override) {
      internalLogger.debug.d4({
        className: 'SessionServiceNative',
        methodName: 'isLoggedIn',
        message: 'Overriding isLoggedIn method with custom implementation',
        isAsyncMethod: false
      });

      return override();
    }

    return !!this._legacySessionDataValue?.isLoggedIn;
  }

  public getIdToken(): string {
    return this._legacySessionDataValue?.idToken;
  }

  private async _updateInitialState(): Promise<void> {
    try {
      this._legacySessionDataValue.idToken = '';

      const isLoggedInData = (await AuthPluginFacade.isLoggedIn()) as {
        value: boolean;
      };
      this._legacySessionDataValue.isLoggedIn = isLoggedInData.value;

      if (isLoggedInData?.value) {
        const tokenData = await InitializationTokenCache.getToken({
          tokenProviderOptions: {
            tokenType: TokenType.user
          }
        });

        this._legacySessionDataValue.idToken = (
          tokenData as AccessToken
        )?.account?.idToken;

        /* istanbul ignore next */
        internalLogger.debug(
          'Legacy session data value updated to:',
          this._legacySessionDataValue
        );

        await this._setStratusAccessTokenFromNative();
      }
    } catch (e) {
      /* istanbul ignore next */
      internalLogger.error('Failed from get Auth data from JWeb.');
      /* istanbul ignore next */
      internalLogger.error(e);
      this._legacySessionDataValue.isLoggedIn = false;
    }
  }

  private async _setStratusAccessTokenFromNative(): Promise<void> {
    try {
      if (this.isLoggedIn()) {
        /* istanbul ignore next */
        internalLogger?.debug?.d2({
          className: 'SessionServiceNative',
          methodName: '_setStratusAccessTokenFromNative',
          message:
            'User is logged in, calling refreshTokenServiceNative.refresh',
          parameters: null
        });
        return this._refreshTokenServiceNative.refresh();
      }

      /* istanbul ignore next */
      internalLogger.debug(
        'User is not logged in yet, skipping setting Stratus token.'
      );
    } catch (error) {
      /* istanbul ignore next */
      internalLogger.error('Error on refresh token');
      /* istanbul ignore next */
      internalLogger.error(error);
      return;
    }
  }
}

export default SessionServiceNative;
